// OOXML style cascade (word/styles.xml).
//
// Word resolves the effective run/paragraph properties by layering, in order:
//   docDefaults  →  paragraph-style chain (basedOn, base-most first)  →
//   character-style chain (runs only)  →  direct pPr/rPr on the node.
// Later layers override earlier ones. This module builds that model once and
// exposes resolvers the extractor calls per paragraph/run.
import { attrByLocalName, childByLocalName, childrenByLocalName, intAttr, val } from './readDocxXml';
import { STYLE_TYPES } from './constants';
import type { ResolvedParagraphProps, ResolvedRunProps } from './types';

// OOXML's ST_OnOff type (ECMA-376 §17.17.4, e.g. `w:default` on `w:style`):
// a boolean-ish attribute value that accepts "1"/"true"/"on" for enabled —
// not "1" alone — and "0"/"false"/"off" for disabled. An absent value
// (`null`, what `attrByLocalName` returns for a missing attribute) is NOT
// "on": `w:default` defaults to off when omitted, so callers pass the raw
// lookup straight through rather than special-casing `null` as enabled.
function isOoxmlOn(value: string | null): boolean {
    return value === '1' || value === 'true' || value === 'on';
}

interface StyleDef {
    type: string;
    basedOn: string | null;
    pPr: Element | null;
    rPr: Element | null;
    /** The style's friendly `w:name` (what mammoth's `style-name` matcher compares against). */
    name: string | null;
}

export interface StylesData {
    styles: Map<string, StyleDef>;
    docDefaultsPPr: Element | null;
    docDefaultsRPr: Element | null;
    /**
     * styleId of the paragraph style marked `w:default="1"` (usually "Normal").
     * Word applies this to every paragraph that has NO explicit `w:pStyle`, so it
     * is the effective base for un-styled body text — resolveRunProps /
     * resolveParagraphProps fall back to it when no style id is given.
     */
    defaultParagraphStyleId: string | null;
}

export function parseStyles(stylesDoc: Document | null): StylesData {
    const styles = new Map<string, StyleDef>();
    let docDefaultsPPr: Element | null = null;
    let docDefaultsRPr: Element | null = null;
    let defaultParagraphStyleId: string | null = null;

    const root = stylesDoc?.documentElement ?? null;
    if (!root) return { styles, docDefaultsPPr, docDefaultsRPr, defaultParagraphStyleId };

    const docDefaults = childByLocalName(root, 'docDefaults');
    if (docDefaults) {
        docDefaultsPPr = childByLocalName(childByLocalName(docDefaults, 'pPrDefault'), 'pPr');
        docDefaultsRPr = childByLocalName(childByLocalName(docDefaults, 'rPrDefault'), 'rPr');
    }

    for (const styleEl of childrenByLocalName(root, 'style')) {
        const id = attrByLocalName(styleEl, 'styleId');
        if (!id) continue;
        const type = attrByLocalName(styleEl, 'type') ?? '';
        styles.set(id, {
            type,
            basedOn: val(childByLocalName(styleEl, 'basedOn')),
            pPr: childByLocalName(styleEl, 'pPr'),
            rPr: childByLocalName(styleEl, 'rPr'),
            name: val(childByLocalName(styleEl, 'name')),
        });
        // The document's default paragraph style (w:type="paragraph", w:default
        // one of OOXML's ST_OnOff "on" spellings — see isOoxmlOn).
        if (type === STYLE_TYPES.PARAGRAPH && isOoxmlOn(attrByLocalName(styleEl, 'default')) && defaultParagraphStyleId == null) {
            defaultParagraphStyleId = id;
        }
    }

    return { styles, docDefaultsPPr, docDefaultsRPr, defaultParagraphStyleId };
}

// Style chain from base-most ancestor to the style itself, so a caller can
// apply them left-to-right and let the derived style win. `visited` guards the
// (malformed but real) case of a basedOn cycle.
function styleChain(styleId: string | null, data: StylesData): StyleDef[] {
    const chain: StyleDef[] = [];
    const visited = new Set<string>();
    let current = styleId;
    while (current && !visited.has(current)) {
        visited.add(current);
        const def = data.styles.get(current);
        if (!def) break;
        chain.push(def);
        current = def.basedOn;
    }
    return chain.reverse();
}

function readRunPr(rPr: Element | null, into: ResolvedRunProps): void {
    if (!rPr) return;

    const sz = val(childByLocalName(rPr, 'sz'));
    if (sz != null) {
        const parsed = parseInt(sz, 10);
        if (Number.isFinite(parsed)) into.sz = parsed;
    }

    const color = val(childByLocalName(rPr, 'color'));
    if (color != null) into.color = color;

    const highlight = val(childByLocalName(rPr, 'highlight'));
    if (highlight != null) into.highlight = highlight;

    const shd = childByLocalName(rPr, 'shd');
    if (shd) {
        const fill = attrByLocalName(shd, 'fill');
        if (fill != null) into.shdFill = fill;
    }

    // <w:u> present with any val other than "none" is an underline; a bare
    // <w:u w:val="none"/> in a later layer explicitly clears an inherited one.
    const u = childByLocalName(rPr, 'u');
    if (u) into.underline = val(u) !== 'none';

    const rFonts = childByLocalName(rPr, 'rFonts');
    if (rFonts) {
        const font = attrByLocalName(rFonts, 'ascii') ?? attrByLocalName(rFonts, 'hAnsi');
        if (font != null) into.font = font;
    }
}

function readParagraphPr(pPr: Element | null, into: ResolvedParagraphProps): void {
    if (!pPr) return;

    const jc = val(childByLocalName(pPr, 'jc'));
    if (jc != null) into.jc = jc;

    const spacing = childByLocalName(pPr, 'spacing');
    if (spacing) {
        into.spacing = into.spacing ?? {};
        const line = intAttr(spacing, 'line');
        if (line != null) into.spacing.line = line;
        const lineRule = attrByLocalName(spacing, 'lineRule');
        if (lineRule != null) into.spacing.lineRule = lineRule;
        const before = intAttr(spacing, 'before');
        if (before != null) into.spacing.before = before;
        const after = intAttr(spacing, 'after');
        if (after != null) into.spacing.after = after;
    }

    const numPr = childByLocalName(pPr, 'numPr');
    if (numPr) {
        const numId = val(childByLocalName(numPr, 'numId'));
        if (numId != null) {
            const ilvlRaw = val(childByLocalName(numPr, 'ilvl'));
            const ilvl = ilvlRaw != null ? parseInt(ilvlRaw, 10) : 0;
            into.numPr = { ilvl: Number.isFinite(ilvl) ? ilvl : 0, numId };
        }
    }

    const ind = childByLocalName(pPr, 'ind');
    if (ind) {
        const left = intAttr(ind, 'left');
        if (left != null) into.indentLeft = left;
        const hanging = intAttr(ind, 'hanging');
        if (hanging != null) into.indentHanging = hanging;
        const firstLine = intAttr(ind, 'firstLine');
        if (firstLine != null) into.indentFirstLine = firstLine;
    }

    const shd = childByLocalName(pPr, 'shd');
    if (shd) {
        const fill = attrByLocalName(shd, 'fill');
        if (fill != null) into.shdFill = fill;
    }
}

export function resolveRunProps(
    paragraphStyleId: string | null,
    runStyleId: string | null,
    directRPr: Element | null,
    data: StylesData,
): ResolvedRunProps {
    const out: ResolvedRunProps = {};
    // A paragraph with no explicit style still inherits the document's default
    // paragraph style ("Normal") — otherwise un-styled body text is compared
    // against docDefaults alone and its true base font/size is missed.
    const effectiveParagraphStyleId = paragraphStyleId ?? data.defaultParagraphStyleId;
    readRunPr(data.docDefaultsRPr, out);
    for (const def of styleChain(effectiveParagraphStyleId, data)) readRunPr(def.rPr, out);
    for (const def of styleChain(runStyleId, data)) readRunPr(def.rPr, out);
    readRunPr(directRPr, out);
    return out;
}

export function resolveParagraphProps(
    paragraphStyleId: string | null,
    directPPr: Element | null,
    data: StylesData,
): ResolvedParagraphProps {
    const out: ResolvedParagraphProps = {};
    const effectiveParagraphStyleId = paragraphStyleId ?? data.defaultParagraphStyleId;
    readParagraphPr(data.docDefaultsPPr, out);
    for (const def of styleChain(effectiveParagraphStyleId, data)) readParagraphPr(def.pPr, out);
    readParagraphPr(directPPr, out);
    return out;
}

export interface HeadingStyleName {
    name: string;
    /** 0-5 (Word's `w:outlineLvl`) — the matching mammoth heading tag is h<level + 1>. */
    level: number;
}

const MIN_OUTLINE_LVL = 0;
const MAX_OUTLINE_LVL = 5;

// Word marks a paragraph style as a HEADING via `w:outlineLvl` — independent of
// the style's own name — so a client-specific style ("CAM Heading 1") is still
// a real heading if it (or a `w:basedOn` ancestor) carries one. Cascades the
// same base-to-derived chain resolveParagraphProps uses, so a level set on an
// ancestor is inherited and a level re-declared on the derived style wins.
export function resolveHeadingStyleNames(data: StylesData): HeadingStyleName[] {
    const out: HeadingStyleName[] = [];
    for (const [styleId, def] of data.styles) {
        if (def.type !== STYLE_TYPES.PARAGRAPH || !def.name) continue;
        let level: number | undefined;
        for (const ancestor of styleChain(styleId, data)) {
            const raw = val(childByLocalName(ancestor.pPr, 'outlineLvl'));
            const parsed = raw != null ? parseInt(raw, 10) : NaN;
            if (Number.isInteger(parsed) && parsed >= MIN_OUTLINE_LVL && parsed <= MAX_OUTLINE_LVL) level = parsed;
        }
        if (level != null) out.push({ name: def.name, level });
    }
    return out;
}
